/**
 * Formats text to title case (first letter of each word capitalized)
 * Handles ALL CAPS, lowercase, and mixed case
 *
 * @example
 * toTitleCase('JOHN SMITH') // 'John Smith'
 * toTitleCase('john smith') // 'John Smith'
 * toTitleCase('John Smith') // 'John Smith'
 * toTitleCase('Quote (copy)') // 'Quote (Copy)'
 */
export const toTitleCase = (text: string | undefined | null): string => {
  if (!text) return '';

  return (
    text
      .toLowerCase()
      .split(' ')
      // Capitalize the first letter, past any leading bracket or quote.
      .map(word =>
        word.replace(
          /^([("'[{]*)(.)/,
          (_, lead: string, first: string) => lead + first.toUpperCase()
        )
      )
      .join(' ')
  );
};

/**
 * Formats customer/account object names to title case
 * Handles both 'name' and 'accountName' fields
 *
 * @param customer - Customer or account object
 * @returns Formatted customer object with title case names
 */
export const formatCustomerNames = (customer: any): any => {
  if (!customer) return customer;

  return {
    ...customer,
    ...(customer.name && { name: toTitleCase(customer.name) }),
    ...(customer.accountName && {
      accountName: toTitleCase(customer.accountName)
    })
  };
};

/**
 * Formats nested account object in orders/invoices/quotes
 *
 * @param data - Data object containing an account field
 * @returns Data with formatted account names
 */
export const formatAccountData = (data: any): any => {
  if (!data) return data;

  if (data.account) {
    return {
      ...data,
      account: formatCustomerNames(data.account)
    };
  }

  return data;
};

/**
 * Formats an array of customer/account data
 *
 * @param customers - Array of customer objects
 * @returns Array of customers with formatted names
 */
export const formatCustomerArray = (customers: any[]): any[] => {
  if (!Array.isArray(customers)) return customers;
  return customers.map(formatCustomerNames);
};
